import {
  formatNumber,
  formatSogUnit,
  formatDistanceUnit,
  speedUnitLabel,
  distanceUnitLabel,
  speedValue,
  distanceValue,
} from '@boatnav/core';
import { useSog, useCog, useGpsStatus } from './useFix.js';
import { useTripDistance } from '../tracks/useRecorder.js';
import { useFollowMetrics } from '../route/useFollow.js';
import { useFollowStore } from '../route/followStore.js';
import { useUnitsStore } from '../../app/unitsStore.js';
import { useDeclinationDeg, formatCourseRef } from './courseFormat.js';
import { msg } from '../../lib/messages.js';
import './instruments.css';

function SogCell(): JSX.Element {
  const sog = useSog();
  const speedUnit = useUnitsStore((s) => s.speed);
  return (
    <div className="cell">
      <div className="cell__label">{msg.nav.sog}</div>
      <div className="cell__value numeral">
        {formatSogUnit(sog, speedUnit)}
        <span className="cell__unit">{speedUnitLabel[speedUnit]}</span>
      </div>
    </div>
  );
}

function CogCell(): JSX.Element {
  const cog = useCog();
  const courseRef = useUnitsStore((s) => s.courseRef);
  const declDeg = useDeclinationDeg();
  return (
    <div className="cell">
      <div className="cell__label">{msg.nav.cog}</div>
      <div className="cell__value numeral">{formatCourseRef(cog, declDeg, courseRef)}</div>
    </div>
  );
}

function TripCell(): JSX.Element {
  const trip = useTripDistance();
  const distanceUnit = useUnitsStore((s) => s.distance);
  const v = distanceValue(trip, distanceUnit);
  return (
    <div className="cell">
      <div className="cell__label">{msg.nav.trip}</div>
      <div className="cell__value numeral">
        {formatNumber(v, v < 10 ? 2 : 1)}
        <span className="cell__unit">{distanceUnitLabel[distanceUnit]}</span>
      </div>
    </div>
  );
}

// When following a route, the third cell shows XTE + distance to the next
// waypoint instead of trip distance (spec 05).
function FollowCell(): JSX.Element {
  const m = useFollowMetrics();
  const distanceUnit = useUnitsStore((s) => s.distance);
  const courseRef = useUnitsStore((s) => s.courseRef);
  const declDeg = useDeclinationDeg();
  const arrow = m == null ? '' : m.xteNm >= 0 ? '▸' : '◂';
  const xte =
    m == null ? '—' : `${arrow} ${formatNumber(distanceValue(Math.abs(m.xteNm), distanceUnit), 2)}`;
  const toWp = m == null ? '—' : formatDistanceUnit(m.toWpNm, distanceUnit);
  const toWpBrg = m == null ? '' : ` · ${formatCourseRef(m.toWpBrg, declDeg, courseRef)}`;
  return (
    <div className="cell">
      <div className="cell__label">
        {msg.planner.xte} · {msg.planner.toWp}
      </div>
      <div className="cell__value cell__value--follow numeral">
        <span className="cell__xte">{xte}</span>
        <span className="cell__towp">
          {toWp}
          {toWpBrg}
        </span>
      </div>
    </div>
  );
}

export function Instruments(): JSX.Element {
  const status = useGpsStatus();
  const following = useFollowStore((s) => s.route != null);
  const dim = status === 'stale' || status === 'lost';
  return (
    <div className={`instruments${dim ? ' instruments--stale' : ''}`}>
      <SogCell />
      <CogCell />
      {following ? <FollowCell /> : <TripCell />}
    </div>
  );
}
